import * as React from 'react';
import { useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import { Spinner } from '~shared/components/Spinner';
import { useGetActivityInsights } from '~teacher/hook/activity/insights/useGetActivityInsights';

import { sleep } from '../../../../../../util/sleep';
import { SelectInsights } from './SelectInsights';
import { SelectQuestions } from './SelectQuestions';

export type AreasOfImprovementStep =
  | 'step1-select-insights'
  | 'step2-select-questions';

interface AreasOfImprovementProps {
  backToGenerationType?: () => void;
  activityInstanceId: number;
}

export function AreasOfImprovement({
  backToGenerationType,
  activityInstanceId,
}: AreasOfImprovementProps) {
  const generateQuestionsMutation =
    useGenerateQuestionsByAreaOfImprovementMutation();
  const [areasOfImprovementStep, setAreaOfImprovementStep] =
    useState<AreasOfImprovementStep>('step1-select-insights');
  const [selectedInsightId, setSelectedInsightId] = useState<Set<number>>(
    new Set()
  );

  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);

  const addInsightId = (insightId: number) => {
    const newSet = new Set([...selectedInsightId]);
    newSet.add(insightId);
    setSelectedInsightId(newSet);
  };

  const removeInsightId = (insightId: number) => {
    const newSet = new Set([...selectedInsightId]);
    newSet.delete(insightId);
    setSelectedInsightId(newSet);
  };

  const [questionsToGenerate, setQuestionsToGenerate] = useState<number>(5);

  const activityInsightsQuery = useGetActivityInsights({
    activityInstanceId,
    select: (data) =>
      data.insights.insights.map(({ type, description }, index) => ({
        type,
        description,
        insightId: index,
      })),
  });

  switch (areasOfImprovementStep) {
    case 'step1-select-insights': {
      if (activityInsightsQuery.data === undefined) {
        return (
          <div className="flex justify-center items-center h-full">
            <Spinner />
          </div>
        );
      }

      return (
        <SelectInsights
          backToGenerationType={backToGenerationType}
          isGeneratingQuestions={isGeneratingQuestions}
          setIsGeneratingQuestions={setIsGeneratingQuestions}
          generateQuestionsMutation={generateQuestionsMutation}
          selectedInsightId={selectedInsightId}
          questionsToGenerate={questionsToGenerate}
          setAreaOfImprovementStep={setAreaOfImprovementStep}
          insights={activityInsightsQuery.data}
          addInsightId={addInsightId}
          removeInsightId={removeInsightId}
          setQuestionsToGenerate={setQuestionsToGenerate}
        />
      );
    }
    case 'step2-select-questions': {
      if (generateQuestionsMutation.isLoading) {
        return (
          <div className="flex justify-center items-center h-full">
            <Spinner />
          </div>
        );
      }
      return (
        <SelectQuestions
          questions={(generateQuestionsMutation.data?.questions ?? []).map(
            ({ questionId, questionType, questionText }) => ({
              questionId,
              type: questionType,
              questionText,
            })
          )}
          onBack={() => setAreaOfImprovementStep('step1-select-insights')}
        />
      );
    }
  }
}

export type GenerateQuestionsByAreaOfImprovementMutation = ReturnType<
  typeof useGenerateQuestionsByAreaOfImprovementMutation
>;

// TODO: actually call the API.
function useGenerateQuestionsByAreaOfImprovementMutation() {
  return useMutation({
    mutationFn: async (params: {
      selectedInsightId: Set<number>;
      questionsToGenerate: number;
    }) => {
      console.log(params);
      await sleep(1000);
      return {
        questions: [
          {
            questionId: 1,
            questionType: 'MC',
            questionText:
              'What does the acronym TEST A stand for in project management?',
          },
          {
            questionId: 2,
            questionType: 'TF',
            questionText:
              'TEST A is typically used in software development phases.',
          },
        ],
      };
    },
  });
}
